/**
 * Temples and authorization — the strongest trust claim the brand makes.
 *
 * "Official temple puja" is permitted **only** with a signed agreement from the temple, trust or
 * board, with a tracked expiry. Without one the only allowed wording is independent-pandit
 * phrasing ("Book a verified pandit in Gaya").
 *
 * **Nothing on this screen sets an "is official" flag, because no such flag exists.** The three
 * booleans shown are derived by the server from the agreements on every read, so a suspension
 * takes effect on the next request and an expiry takes effect on its own. The live/after panels
 * therefore compare what the website *says today* against what it would say — not two states of
 * a checkbox.
 *
 * **A renewal is a new agreement.** Nothing leaves `expired` or `terminated`; the history keeps
 * the gap where cover had lapsed, because that gap is the period a regulator asks about.
 *
 * **Publishing is guarded and the server names what is missing.** The blockers arrive as a list,
 * so the checklist here is the server's answer rather than a second copy of the rule.
 */
import { useState } from 'react';
import { Badge, Card, Icon, Kicker, Note, StatusChip } from '../components/primitives';
import { DataTable, DetailPage } from '../components/data';
import { FileField, Input, Select, Toggle } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, FilterBar, ScopeAction, Section } from '../components/kit';
import { AuditTrailPanel } from '../components/audit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useUrlTab } from '../lib/filterCodecs';
import type { components } from '../api/generated/schema';
import {
  useAuthorizationTransition,
  useAuthorizations,
  useCreateAuthorization,
  useExpiringAuthorizations,
  useRestoreTemple,
  useRetireTemple,
  useSetTempleStatus,
  useTemple,
  useTemples,
  useDownloadAgreementDocument,
  usePatchTemple,
  useReplaceFestivals,
  useReplaceTimings,
  useUploadAgreementDocument,
  useAddTempleMedia,
  useDeleteTempleMedia,
  usePatchTempleMedia,
  MEDIA_KINDS,
  type AuthorizationRow,
  type TransitionAction,
} from '../lib/api/hooks/temples';
import { useReplaceTempleDeities } from '../lib/api/hooks/temples';
import { useAxisValues } from '../lib/api/hooks/catalogue';
import { LinkPicker } from './PujaCatalogue';
import { NewTempleForm } from './CatalogueCreate';
import { TempleClosures, TempleRituals, TempleSources, TempleVerification } from './TempleResearch';

type TimingIn = components['schemas']['TimingIn'];
type FestivalIn = components['schemas']['FestivalIn'];

/* The read rows carry their `id`; the replace bodies declare none, and a strict schema refuses a
   key it does not declare (R74). Copy the declared fields, so a stored row saves as it reads. */
function toTimingIn({ kind, opens_at, closes_at, day_of_week, label, note, sort_order }: TimingIn): TimingIn {
  return { kind, opens_at, closes_at, day_of_week, label, note, sort_order };
}

function toFestivalIn({
  festival_id,
  name,
  occasion_id,
  month_note,
  tithi_note,
  description,
  sort_order,
}: FestivalIn): FestivalIn {
  return { festival_id, name, occasion_id, month_note, tithi_note, description, sort_order };
}

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Draft' },
  { key: 'archived', label: 'Archived' },
];
const TAB_KEYS = TABS.map((t) => t.key);

/* Pujas, Sources, Closures and Verification are CSR-123's, built from the spec (`TempleResearch.tsx`). */
const DETAIL_TABS = [
  'Authorization', 'Editorial', 'Deities', 'Pujas', 'Timings', 'Festivals', 'Closures', 'Imagery', 'Public copy', 'Sources', 'Verification', 'Publishing', 'History',
];

/**
 * The long-form fields, in the order an editor fills them.
 *
 * ⛔ **Nothing in this list touches the official-wording claim.** That is derived from the
 * agreements on every read and appears and disappears on its own — `PATCH /v1/admin/temples/{id}`
 * carries no authorization field at all, which is what stops an editor asserting it in passing.
 */
const EDITORIAL: readonly { key: EditorialKey; label: string; long?: boolean; hint?: string }[] = [
  { key: 'founded_era', label: 'Founded (era)', hint: 'As the temple states it — "12th century", not a date we inferred' },
  { key: 'history', label: 'History', long: true },
  { key: 'significance', label: 'Significance', long: true, hint: 'Traditional and scriptural only — never an outcome a devotee can expect' },
  { key: 'architecture', label: 'Architecture', long: true },
  { key: 'how_to_reach', label: 'How to reach', long: true },
  { key: 'nearest_railway_station', label: 'Nearest railway station' },
  { key: 'nearest_airport', label: 'Nearest airport' },
  { key: 'entry_notes', label: 'Entry notes', long: true },
  { key: 'contact_phone', label: 'Contact phone' },
  { key: 'seo_title', label: 'SEO title', hint: 'Left empty, the exporter falls back to the page’s own heading' },
  { key: 'seo_description', label: 'SEO description', long: true },
];

type EditorialKey =
  | 'founded_era'
  | 'history'
  | 'significance'
  | 'architecture'
  | 'how_to_reach'
  | 'nearest_railway_station'
  | 'nearest_airport'
  | 'entry_notes'
  | 'contact_phone'
  | 'seo_title'
  | 'seo_description';

/** `0 = Monday … 6 = Sunday`; `null` is every day, which is most rows. */
const DAYS = ['Every day', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** Server-side, always. `all` is the absence of a filter, not a filter for everything. */
export function tabToStatus(tab: string): string | undefined {
  return tab === 'all' ? undefined : tab;
}

export function statusTone(status: string): 'success' | 'warn' | 'neutral' | 'error' {
  if (status === 'active' || status === 'published') return 'success';
  if (status === 'draft' || status === 'pending_signature' || status === 'suspended') return 'warn';
  if (status === 'terminated' || status === 'expired') return 'error';
  return 'neutral';
}

/**
 * Which transitions this agreement can actually make, from the server's own edge list.
 *
 * Offering a button the server will refuse teaches an operator that the console guesses. Offering
 * none where one is legal makes them think the feature is missing.
 */
export function availableActions(status: string): TransitionAction[] {
  if (status === 'draft') return ['submit'];
  if (status === 'pending_signature') return ['activate', 'terminate'];
  if (status === 'active') return ['suspend', 'terminate'];
  if (status === 'suspended') return ['resume', 'terminate'];
  return [];
}

/** The two that change what a temple may be called need a reason in the audit log. */
const NEEDS_REASON: ReadonlySet<TransitionAction> = new Set(['suspend', 'terminate']);

const ACTION_LABEL: Record<TransitionAction, string> = {
  submit: 'Send for signature',
  activate: 'Activate',
  suspend: 'Suspend',
  resume: 'Resume',
  terminate: 'Terminate',
};

const COUNTERPARTY_KINDS = [
  'trust',
  'board',
  'temple_committee',
  'authorized_operator',
  'government_body',
];

/** The three scopes the predicate answers for, in the doc's words (doc 05-admin/13 §2). */
const SCOPE_LABEL: Record<string, string> = {
  official_puja: 'Official puja',
  official_prasad: 'Official prasad',
  seva_listing: 'Seva listing',
};

/** What each scope lets the site say once it is granted. */
const OFFICIAL_PHRASE: Record<string, (name: string) => string> = {
  official_puja: (name) => `Official ${name} Puja`,
  official_prasad: (name) => `Official ${name} prasad`,
  seva_listing: (name) => `Seva at ${name}, listed with the temple`,
};

/** The predicate's typed refusals, said so the next piece of work is obvious. */
const DENY_REASON: Record<string, string> = {
  no_authorization: 'no authorization',
  not_active: 'no agreement is active',
  out_of_window: 'outside the agreement\u2019s dates',
  scope_not_granted: 'scope not granted',
};

/**
 * The predicate scope by scope (doc 05-admin/13 §2): what the site says now and, when it says
 * the independent phrasing, why — so the operator knows which piece of work unlocks the claim.
 */
function WordingList({
  wording,
  name,
}: {
  wording: readonly { scope: string; allowed: boolean; deny_reason?: string | null; expires_on?: string | null }[];
  name: string;
}) {
  if (wording.length === 0) return null;
  return (
    <ul aria-label="Public wording, scope by scope" style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
      {wording.map((w) => (
        <li key={w.scope} style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
          <strong>{SCOPE_LABEL[w.scope] ?? w.scope}:</strong>{' '}
          {w.allowed
            ? `\u201c${OFFICIAL_PHRASE[w.scope]?.(name) ?? `Official ${name}`}\u201d \u2713 (active until ${w.expires_on ?? 'the agreement expires'})`
            : `independent phrasing \u2014 ${DENY_REASON[w.deny_reason ?? ''] ?? w.deny_reason ?? 'not permitted'}`}
        </li>
      ))}
    </ul>
  );
}

export function Temples() {
  const { scope, toast } = useConsole();
  const [tab, setTab] = useUrlTab('temples', TAB_KEYS, 'all');
  const [openId, setOpenId] = useState<number | undefined>();
  const [creating, setCreating] = useState(false);
  const [detailTab, setDetailTab] = useState('Authorization');
  const [confirm, setConfirm] = useState<{ id: number; action: TransitionAction } | null>(null);
  const [counterparty, setCounterparty] = useState('');
  const [kind, setKind] = useState('trust');
  /* The scope is chosen when the agreement is drafted, not bolted on later: activation refuses
     an agreement that grants nothing, and "which claim did we actually license" is the question
     the whole table exists to answer. */
  const [scopes, setScopes] = useState({ puja: true, prasad: false, seva: false });

  const temples = useTemples({
    page: 1,
    page_size: 100,
    status: tabToStatus(tab),
    include_deleted: tab === 'archived',
  });
  const temple = useTemple(openId);
  const authorizations = useAuthorizations(openId);
  const expiring = useExpiringAuthorizations();

  const setStatus = useSetTempleStatus();
  const retire = useRetireTemple();
  const [retiring, setRetiring] = useState(false);
  const restore = useRestoreTemple();
  const [restoring, setRestoring] = useState(false);
  const createAuthorization = useCreateAuthorization();
  const transition = useAuthorizationTransition();
  const upload = useUploadAgreementDocument();
  const patchTemple = usePatchTemple();
  const replaceTimings = useReplaceTimings();
  const replaceFestivals = useReplaceFestivals();
  const replaceDeities = useReplaceTempleDeities();
  /* The deity names are the taxonomy's, edited on its own screen — read here, never restated. */
  const deityOptions = useAxisValues('deities');
  /* Staged, keyed on the temple: these are PATCH and replace-the-whole-list writes, and a field
     that saved on every keystroke would send half-typed prose to a public page. */
  const [copy, setCopy] = useState<Partial<Record<EditorialKey, string>>>({});
  const [timingRows, setTimingRows] = useState<TimingIn[] | null>(null);
  const [festivalRows, setFestivalRows] = useState<FestivalIn[] | null>(null);
  const download = useDownloadAgreementDocument();
  const addMedia = useAddTempleMedia();
  const patchMedia = usePatchTempleMedia();
  const deleteMedia = useDeleteTempleMedia();
  const [mediaDraft, setMediaDraft] = useState({ kind: 'photo', alt: '', caption: '' });
  const [altEdits, setAltEdits] = useState<Record<number, string>>({});

  const fail = (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) });

  const rows = temples.data?.items ?? [];
  const agreements: AuthorizationRow[] = authorizations.data?.items ?? [];
  const detail = temple.data;
  const pending = confirm && agreements.find((a) => a.id === confirm.id);
  /* The staged set, or the server's. `null` is what makes Save inert — an empty array is a real
     edit meaning "this temple keeps none". */
  const timings: TimingIn[] = timingRows ?? (detail?.timings ?? []).map(toTimingIn);
  const festivals: FestivalIn[] = festivalRows ?? (detail?.festivals ?? []).map(toFestivalIn);
  const editedCopy = EDITORIAL.some(
    (f) => copy[f.key] !== undefined && copy[f.key] !== (detail?.[f.key] ?? ''),
  );

  const runTransition = (id: number, action: TransitionAction, reason?: string) =>
    transition.mutate(
      { authorizationId: id, action, reason },
      {
        onSuccess: () =>
          toast({
            tone: 'success',
            message:
              action === 'activate'
                ? 'Activated · the temple may now be described as official'
                : `${ACTION_LABEL[action]} recorded`,
          }),
        onError: fail,
      },
    );

  if (openId !== undefined) {
    return (
      <>
        <DetailPage
          title={detail?.name ?? 'Temple'}
          code={detail?.slug}
          status={detail ? <StatusChip tone={statusTone(detail.status)} label={detail.status} /> : undefined}
          tabs={DETAIL_TABS}
          activeTab={detailTab}
          onTab={setDetailTab}
          onClose={() => setOpenId(undefined)}
          audit="Every change to this temple is audited with the admin who made it, and the reason where one is asked."
        >
          {temple.isPending && <LoadingState message="Reading the temple…" />}
          {temple.isError && (
            <ErrorState title="Could not read this temple" cause={errorMessage(temple.error)} />
          )}

          {detail && detailTab === 'Authorization' && (
            <div style={{ display: 'grid', gap: 'var(--sp-lg)' }}>
              <Section
                title="Agreements"
                note="A renewal is a new agreement — the gap where cover lapsed stays visible"
                pad={false}
              >
                {authorizations.isPending && <LoadingState message="Reading agreements…" />}
                {authorizations.isError && (
                  <ErrorState
                    title="Could not read agreements"
                    cause={errorMessage(authorizations.error)}
                  />
                )}
                {authorizations.isSuccess && agreements.length === 0 && (
                  <EmptyState
                    icon="building"
                    title="No agreement has ever been signed"
                    body="Until one is active this temple may only be described in independent-pandit wording."
                    inline
                  />
                )}
                {agreements.length > 0 && (
                  <DataTable
                    density="compact"
                    columns={[
                      { key: 'counterparty', label: 'Counterparty' },
                      { key: 'status', label: 'State' },
                      { key: 'scope', label: 'Grants' },
                      { key: 'expiry', label: 'Expires' },
                      { key: 'act', label: '' },
                    ]}
                    rows={agreements.map((a) => ({
                      id: a.id,
                      counterparty: (
                        <span>
                          {a.counterparty_name}
                          <span style={{ color: 'var(--c-ink3)' }}> · {a.counterparty_kind}</span>
                        </span>
                      ),
                      status: <StatusChip tone={statusTone(a.status)} label={a.status} />,
                      scope: (
                        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink2)' }}>
                          {[
                            a.allows_official_puja && 'puja',
                            a.allows_official_prasad && 'prasad',
                            a.allows_seva_listing && 'seva',
                          ]
                            .filter(Boolean)
                            .join(' · ') || 'nothing yet'}
                        </span>
                      ),
                      expiry: (
                        <span className="mono-num">
                          {a.expires_on ?? '—'}
                          {a.days_to_expiry !== null && a.days_to_expiry !== undefined && (
                            <span style={{ color: 'var(--c-ink3)' }}> ({a.days_to_expiry}d)</span>
                          )}
                        </span>
                      ),
                      act: (
                        <div style={{ display: 'flex', gap: 'var(--sp-xs)', flexWrap: 'wrap' }}>
                          {availableActions(a.status).map((action) => (
                            <ScopeAction
                              key={action}
                              scope={scope}
                              need="catalogue_content"
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                NEEDS_REASON.has(action)
                                  ? setConfirm({ id: a.id, action })
                                  : runTransition(a.id, action)
                              }
                            >
                              {ACTION_LABEL[action]}
                            </ScopeAction>
                          ))}
                          {availableActions(a.status).length === 0 && (
                            <span style={{ color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' }}>
                              terminal — renew with a new agreement
                            </span>
                          )}
                        </div>
                      ),
                    }))}
                  />
                )}
              </Section>

              <Section
                title="The signed paper"
                note="Activation refuses without it — a row saying the agreement exists is not the agreement"
              >
                {agreements.length === 0 ? (
                  <span style={{ color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-cell)' }}>
                    Start an agreement below first.
                  </span>
                ) : (
                  <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
                    {agreements.map((a) => (
                      <div
                        key={a.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--sp-md)',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span style={{ fontSize: 'var(--fs-admin-cell)' }}>
                          {a.counterparty_name}
                        </span>
                        {/*
                          The count on its own was the whole rendering until now, and a count is
                          not evidence — "official temple" wording is a claim we have to be able
                          to support, and the support is a paper somebody has to be able to open.
                          Each row opens; the link is minted per click and expires.
                        */}
                        {a.documents.length === 0 ? (
                          <span
                            style={{
                              fontSize: 'var(--fs-admin-colhead)',
                              color: 'var(--c-ink3)',
                            }}
                          >
                            no paper on file — activation will refuse
                          </span>
                        ) : (
                          a.documents.map((d) => (
                            <ScopeAction
                              key={d.id}
                              scope={scope}
                              need="catalogue_content"
                              size="sm"
                              variant="ghost"
                              disabled={download.isPending}
                              onClick={() =>
                                download.mutate(
                                  { authorizationId: a.id, documentId: d.id },
                                  {
                                    onSuccess: (link) =>
                                      window.open(link.download_url, '_blank', 'noopener'),
                                    onError: fail,
                                  },
                                )
                              }
                            >
                              Open {d.title}
                            </ScopeAction>
                          ))
                        )}
                        <FileField
                          label="Upload signed agreement"
                          inputLabel={`Signed agreement for ${a.counterparty_name}`}
                          pending={upload.isPending}
                          audited
                          onFile={(file) =>
                            upload.mutate(
                              { authorizationId: a.id, file, kind: 'signed_agreement', title: file.name },
                              { onSuccess: () => toast({ tone: 'success', message: 'Agreement recorded' }), onError: fail },
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              <Section title="Start an agreement" note="This is also the renewal route">
                <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
                  <Input
                    label="Counterparty (legal name)"
                    value={counterparty}
                    onChange={setCounterparty}
                    style={{ minWidth: 280 }}
                  />
                  <Select
                    label="Who signs"
                    value={kind}
                    options={COUNTERPARTY_KINDS}
                    onChange={setKind}
                  />
                  <div style={{ display: 'grid', gap: 4 }}>
                    <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                      What it grants
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--sp-md)' }}>
                      {(['puja', 'prasad', 'seva'] as const).map((s) => (
                        <Toggle
                          key={s}
                          label={`Official ${s}`}
                          on={scopes[s]}
                          onChange={(on) => setScopes((prev) => ({ ...prev, [s]: on }))}
                        />
                      ))}
                    </div>
                  </div>
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    icon="plus"
                    disabled={counterparty.trim().length === 0}
                    onClick={() =>
                      createAuthorization.mutate(
                        {
                          templeId: openId,
                          body: {
                            counterparty_name: counterparty.trim(),
                            counterparty_kind: kind,
                            allows_official_puja: scopes.puja,
                            allows_official_prasad: scopes.prasad,
                            allows_seva_listing: scopes.seva,
                          },
                        },
                        {
                          onSuccess: () => {
                            setCounterparty('');
                            toast({ tone: 'success', message: 'Draft agreement started' });
                          },
                          onError: fail,
                        },
                      )
                    }
                  >
                    Start
                  </ScopeAction>
                </div>
              </Section>
            </div>
          )}

          {detail && detailTab === 'Editorial' && (
            <Section
              title="What the page says about this temple"
              note="Long-form content, saved as one PATCH — nothing here asserts an agreement"
            >
              <Note>
                Significance is traditional and scriptural. Never an outcome a devotee can expect
                from a visit — the server's content lint refuses that and says which rule it broke.
              </Note>
              {EDITORIAL.map((field) => (
                <Input
                  key={field.key}
                  density="admin"
                  multiline={field.long}
                  label={field.label}
                  helper={field.hint}
                  value={copy[field.key] ?? detail[field.key] ?? ''}
                  onChange={(v) => setCopy({ ...copy, [field.key]: v })}
                />
              ))}
              <ScopeAction
                scope={scope}
                need="catalogue_content"
                size="sm"
                disabled={!editedCopy}
                onClick={() =>
                  patchTemple.mutate(
                    { templeId: detail.id, body: copy },
                    {
                      onSuccess: () => {
                        setCopy({});
                        toast({ tone: 'success', message: 'Copy saved' });
                      },
                      onError: fail,
                    },
                  )
                }
              >
                Save copy
              </ScopeAction>
            </Section>
          )}

          {detail && detailTab === 'Timings' && (
            <Section title="When the temple is open" note="Local wall-clock, never a timezone-bearing moment">
              <Note>
                A row with no day applies every day, which is most of them. Order is the list
                order, and the whole set is replaced on save.
              </Note>
              {timings.length === 0 && (
                <EmptyState icon="clock" title="No timings recorded" body="The page shows none." inline />
              )}
              {timings.map((row, index) => (
                <div
                  key={index}
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr)) auto', gap: 'var(--sp-md)', alignItems: 'end' }}
                >
                  <Input
                    density="admin"
                    label="Kind"
                    value={row.kind}
                    onChange={(v) => setTimingRows(timings.map((r, i) => (i === index ? { ...r, kind: v } : r)))}
                  />
                  <Input
                    density="admin"
                    label="Opens"
                    value={row.opens_at}
                    placeholder="05:30:00"
                    onChange={(v) => setTimingRows(timings.map((r, i) => (i === index ? { ...r, opens_at: v } : r)))}
                  />
                  <Input
                    density="admin"
                    label="Closes"
                    value={row.closes_at ?? ''}
                    placeholder="21:00:00"
                    onChange={(v) =>
                      setTimingRows(timings.map((r, i) => (i === index ? { ...r, closes_at: v || null } : r)))
                    }
                  />
                  <Select
                    density="admin"
                    label="Day"
                    options={DAYS}
                    value={DAYS[(row.day_of_week ?? -1) + 1]}
                    onChange={(v) =>
                      setTimingRows(
                        timings.map((r, i) =>
                          i === index ? { ...r, day_of_week: DAYS.indexOf(v) === 0 ? null : DAYS.indexOf(v) - 1 } : r,
                        ),
                      )
                    }
                  />
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="ghost"
                    onClick={() => setTimingRows(timings.filter((_, i) => i !== index))}
                  >
                    Remove
                  </ScopeAction>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 'var(--sp-md)' }}>
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setTimingRows([
                      ...timings,
                      { kind: 'darshan', opens_at: '', closes_at: null, day_of_week: null, label: null, note: null, sort_order: timings.length },
                    ])
                  }
                >
                  Add a timing
                </ScopeAction>
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  disabled={timingRows === null}
                  onClick={() =>
                    replaceTimings.mutate(
                      { templeId: detail.id, items: timings.map((r, i) => ({ ...r, sort_order: i })) },
                      {
                        onSuccess: () => {
                          setTimingRows(null);
                          toast({ tone: 'success', message: 'Timings saved' });
                        },
                        onError: fail,
                      },
                    )
                  }
                >
                  Save timings
                </ScopeAction>
              </div>
            </Section>
          )}

          {detail && detailTab === 'Festivals' && (
            <Section title="Festivals this temple keeps" note="A month and a tithi, never a date we invented">
              <Note>
                There is no panchang source in this system, so a festival carries the temple's own
                words for when it falls — not a calculated date the page would state as fact.
              </Note>
              {festivals.length === 0 && (
                <EmptyState icon="calendar-check" title="No festivals recorded" body="The page shows none." inline />
              )}
              {festivals.map((row, index) => (
                <div
                  key={index}
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr)) auto', gap: 'var(--sp-md)', alignItems: 'end' }}
                >
                  <Input
                    density="admin"
                    label="Name"
                    value={row.name}
                    onChange={(v) => setFestivalRows(festivals.map((r, i) => (i === index ? { ...r, name: v } : r)))}
                  />
                  <Input
                    density="admin"
                    label="Month"
                    value={row.month_note ?? ''}
                    placeholder="Kartik"
                    onChange={(v) =>
                      setFestivalRows(festivals.map((r, i) => (i === index ? { ...r, month_note: v || null } : r)))
                    }
                  />
                  <Input
                    density="admin"
                    label="Tithi"
                    value={row.tithi_note ?? ''}
                    placeholder="Purnima"
                    onChange={(v) =>
                      setFestivalRows(festivals.map((r, i) => (i === index ? { ...r, tithi_note: v || null } : r)))
                    }
                  />
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="ghost"
                    onClick={() => setFestivalRows(festivals.filter((_, i) => i !== index))}
                  >
                    Remove
                  </ScopeAction>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 'var(--sp-md)' }}>
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setFestivalRows([
                      ...festivals,
                      { name: '', occasion_id: null, month_note: null, tithi_note: null, description: null, sort_order: festivals.length },
                    ])
                  }
                >
                  Add a festival
                </ScopeAction>
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  disabled={festivalRows === null}
                  onClick={() =>
                    replaceFestivals.mutate(
                      { templeId: detail.id, items: festivals.map((r, i) => ({ ...r, sort_order: i })) },
                      {
                        onSuccess: () => {
                          setFestivalRows(null);
                          toast({ tone: 'success', message: 'Festivals saved' });
                        },
                        onError: fail,
                      },
                    )
                  }
                >
                  Save festivals
                </ScopeAction>
              </div>
            </Section>
          )}

          {detail && detailTab === 'Pujas' && <TempleRituals detail={detail} />}
          {detail && detailTab === 'Closures' && <TempleClosures detail={detail} />}
          {detail && detailTab === 'Sources' && <TempleSources detail={detail} />}
          {detail && detailTab === 'Verification' && <TempleVerification detail={detail} />}

          {detail && detailTab === 'Imagery' && (
            <Section title="Imagery" note="Alt text is required — the page is read aloud as often as it is seen">
              <Note>
                An image goes on the page only after storage confirms the file arrived. An upload
                that failed stays hidden here until it is removed.
              </Note>
              {detail.media.length === 0 && (
                <EmptyState icon="image" title="No imagery yet" body="Publishing asks for a hero image." inline />
              )}
              {detail.media.map((m) => {
                const alt = altEdits[m.id] ?? m.alt_text;
                return (
                  <div
                    key={m.id}
                    style={{ display: 'grid', gridTemplateColumns: 'auto minmax(200px, 1fr) auto auto auto', gap: 'var(--sp-md)', alignItems: 'end' }}
                  >
                    <StatusChip tone={m.is_public ? 'success' : 'neutral'} label={`${m.kind} · ${m.is_public ? 'shown' : 'hidden'}`} />
                    <Input
                      density="admin"
                      label={`Alt text for ${m.kind} ${m.id}`}
                      value={alt}
                      onChange={(v) => setAltEdits((prev) => ({ ...prev, [m.id]: v }))}
                    />
                    <Toggle
                      label={`Show ${m.kind} ${m.id} on the page`}
                      on={m.is_public}
                      onChange={(on) =>
                        patchMedia.mutate({ mediaId: m.id, body: { is_public: on } }, { onError: fail })
                      }
                    />
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="secondary"
                      disabled={alt.trim() === '' || alt === m.alt_text}
                      onClick={() =>
                        patchMedia.mutate(
                          { mediaId: m.id, body: { alt_text: alt } },
                          {
                            onSuccess: () => toast({ tone: 'success', message: 'Alt text saved' }),
                            onError: fail,
                          },
                        )
                      }
                    >
                      Save alt text
                    </ScopeAction>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        deleteMedia.mutate(
                          { mediaId: m.id },
                          {
                            onSuccess: () => toast({ tone: 'success', message: 'Image removed' }),
                            onError: fail,
                          },
                        )
                      }
                    >
                      Remove
                    </ScopeAction>
                  </div>
                );
              })}
              <div
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--sp-md)', alignItems: 'end' }}
              >
                {/* The server refuses a second hero; offering it would be a button that only ever fails. */}
                <Select
                  density="admin"
                  label="Kind"
                  value={mediaDraft.kind}
                  options={MEDIA_KINDS.filter((k) => k !== 'hero' || !detail.media.some((m) => m.kind === 'hero'))}
                  onChange={(v) => setMediaDraft((d) => ({ ...d, kind: v }))}
                />
                <Input
                  density="admin"
                  label="Alt text"
                  value={mediaDraft.alt}
                  placeholder="What the image shows"
                  onChange={(v) => setMediaDraft((d) => ({ ...d, alt: v }))}
                />
                <Input
                  density="admin"
                  label="Caption"
                  value={mediaDraft.caption}
                  onChange={(v) => setMediaDraft((d) => ({ ...d, caption: v }))}
                />
                <FileField
                  label="Upload"
                  inputLabel="Image file"
                  accept={mediaDraft.kind === 'video' ? 'video/*' : 'image/*'}
                  disabled={mediaDraft.alt.trim() === '' || addMedia.isPending}
                  pending={addMedia.isPending}
                  onFile={(file) =>
                    addMedia.mutate(
                      {
                        templeId: detail.id,
                        file,
                        kind: mediaDraft.kind,
                        altText: mediaDraft.alt.trim(),
                        caption: mediaDraft.caption.trim(),
                      },
                      {
                        onSuccess: () => {
                          setMediaDraft({ kind: 'photo', alt: '', caption: '' });
                          toast({ tone: 'success', message: 'Image uploaded and shown' });
                        },
                        onError: fail,
                      },
                    )
                  }
                />
              </div>
            </Section>
          )}

          {detail && detailTab === 'Public copy' && (
            <>
              <div
                style={{
                  background: 'var(--c-warn-bg)',
                  color: 'var(--c-warn-text)',
                  border: '1px solid var(--c-warn)',
                  borderRadius: 'var(--r-tile)',
                  padding: 'var(--sp-md)',
                  fontSize: 'var(--fs-admin-cell)',
                }}
              >
                The left panel is what the website says today, read from the agreements. Nothing
                here is a switch — the claim appears when an agreement is active and disappears on
                its own when it expires.
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: 'var(--sp-md)',
                }}
              >
                {[
                  { label: 'Live copy', authorized: detail.authorization.official_puja },
                  { label: 'With an active agreement', authorized: true },
                ].map((panel) => (
                  <div key={panel.label} style={{ display: 'grid', gap: 6 }}>
                    <Kicker style={panel.authorized ? { color: 'var(--c-saffron-text)' } : undefined}>
                      {panel.label}
                    </Kicker>
                    <Card
                      variant="flat"
                      padding="var(--sp-md)"
                      style={{
                        display: 'grid',
                        gap: 'var(--sp-sm)',
                        borderColor: panel.authorized ? 'var(--c-saffron)' : 'var(--c-border)',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 'var(--fs-admin-card-heading)',
                          fontWeight: 'var(--fw-semibold)',
                          color: 'var(--c-ink)',
                        }}
                      >
                        {detail.name}
                      </div>
                      {panel.authorized && (
                        <span style={{ display: 'inline-flex' }}>
                          <Badge type="temple_authorized" />
                        </span>
                      )}
                      <div style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                        {panel.authorized
                          ? `Official ${detail.name} puja, booked through the temple's own agreement.`
                          : `Book a verified pandit near ${detail.name}.`}
                      </div>
                      <div style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                        {panel.authorized
                          ? `Permitted until ${detail.authorization.valid_until ?? 'the agreement expires'}.`
                          : 'No agreement is active, so no official wording is permitted.'}
                      </div>
                    </Card>
                  </div>
                ))}
              </div>
              <WordingList wording={detail.wording ?? []} name={detail.name} />
            </>
          )}

          {detail && detailTab === 'Deities' && (
            <LinkPicker
              scope={scope}
              resetKey={detail.id}
              title="Deities"
              note="Exactly one is primary — it is the deity the temple page names first."
              /* A retired deity is not offered for a new link; one this temple already has stays,
                 so saving the set does not silently drop it. */
              options={(deityOptions.data?.items ?? [])
                .filter((o) => o.is_active || detail.deities.some((x) => x.deity_id === o.id))
                .map((o) => ({ id: o.id, label: o.name_en }))}
              loading={deityOptions.isPending}
              failed={deityOptions.isError}
              selected={detail.deities.map((x) => x.deity_id)}
              primaryId={detail.deities.find((x) => x.is_primary)?.deity_id ?? null}
              withPrimary
              onSave={(ids, primary) =>
                replaceDeities.mutate(
                  {
                    templeId: detail.id,
                    items: ids.map((id, i) => ({ deity_id: id, is_primary: id === primary, sort_order: i })),
                  },
                  { onSuccess: () => toast({ tone: 'success', message: 'Deities saved' }), onError: fail },
                )
              }
            />
          )}

          {detail && detailTab === 'History' && <AuditTrailPanel entityType="temple" entityId={detail.id} />}
          {detail && detailTab === 'Publishing' && (
            <Section
              title="Before this page can be indexed"
              note="The server decides this list; the console shows it"
            >
              {detail.publish_blockers.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--sp-sm)',
                    alignItems: 'center',
                    fontSize: 'var(--fs-admin-ui)',
                  }}
                >
                  <Icon name="check" size={16} style={{ color: 'var(--c-success)' }} />
                  Nothing is missing.
                </div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 'var(--sp-lg)', color: 'var(--c-ink2)' }}>
                  {detail.publish_blockers.map((blocker) => (
                    <li key={blocker} style={{ fontSize: 'var(--fs-admin-ui)' }}>
                      {blocker}
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: 'flex', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  icon="upload"
                  disabled={detail.publish_blockers.length > 0 || detail.status === 'published'}
                  onClick={() =>
                    setStatus.mutate(
                      { templeId: detail.id, status: 'published' },
                      {
                        onSuccess: () => toast({ tone: 'success', message: 'Published' }),
                        onError: fail,
                      },
                    )
                  }
                >
                  Publish
                </ScopeAction>
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="secondary"
                  disabled={detail.status !== 'published'}
                  onClick={() =>
                    setStatus.mutate(
                      { templeId: detail.id, status: 'draft' },
                      {
                        onSuccess: () =>
                          toast({ tone: 'info', message: 'Back to draft · the page is offline' }),
                        onError: fail,
                      },
                    )
                  }
                >
                  Unpublish
                </ScopeAction>
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="secondary"
                  disabled={detail.status === 'archived'}
                  onClick={() => setRetiring(true)}
                >
                  Archive
                </ScopeAction>
                {detail.status === 'archived' && (
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="secondary"
                    onClick={() => setRestoring(true)}
                  >
                    Restore
                  </ScopeAction>
                )}
              </div>
            </Section>
          )}
        </DetailPage>

        <ConfirmAction
          open={confirm !== null}
          title={
            confirm?.action === 'terminate'
              ? 'Terminate this agreement?'
              : 'Suspend this agreement?'
          }
          phrase={confirm?.action === 'terminate' ? 'TERMINATE' : 'SUSPEND'}
          tone="error"
          reason="Why, in the words the temple would be told"
          consequences={[
            'Official wording stops on the very next page render — there is no grace period.',
            'Every temple-authorized badge resting on this agreement is revoked with it.',
            confirm?.action === 'terminate'
              ? 'Terminating is final. A renewal is a new agreement, and the gap stays in the history.'
              : 'Suspension is reversible — resume puts the wording back.',
            pending ? `Counterparty: ${pending.counterparty_name}.` : '',
          ].filter(Boolean)}
          onCancel={() => setConfirm(null)}
          onConfirm={(reason) => {
            const target = confirm!;
            setConfirm(null);
            runTransition(target.id, target.action, reason);
          }}
          confirmLabel={confirm?.action === 'terminate' ? 'Terminate' : 'Suspend'}
        />

        <ConfirmAction
          open={retiring}
          title={`Archive ${detail?.name ?? 'this temple'}?`}
          phrase="ARCHIVE"
          tone="error"
          reason="Why it is being archived"
          consequences={[
            'The page leaves the site on the next export, and the directory lists it under Archived.',
            'The server refuses while an agreement is active — terminate that first.',
          ]}
          onCancel={() => setRetiring(false)}
          onConfirm={(reason) => {
            setRetiring(false);
            retire.mutate(
              { templeId: detail!.id, reason },
              {
                onSuccess: () => {
                  toast({ tone: 'info', message: `${detail!.name} archived` });
                  setOpenId(undefined);
                },
                onError: fail,
              },
            );
          }}
          confirmLabel="Archive"
        />

        <ConfirmAction
          open={restoring}
          title={`Restore ${detail?.name ?? 'this temple'}?`}
          phrase="RESTORE"
          reason="Why it is coming back"
          consequences={[
            'It comes back as a draft, so the publish gate runs again before the page goes live.',
            'The server refuses while a live temple holds the same slug.',
          ]}
          onCancel={() => setRestoring(false)}
          onConfirm={(reason) => {
            setRestoring(false);
            restore.mutate(
              { templeId: detail!.id, reason },
              {
                onSuccess: () => toast({ tone: 'info', message: `${detail!.name} is back as a draft` }),
                onError: fail,
              },
            );
          }}
          confirmLabel="Restore"
        />
      </>
    );
  }

  if (creating) {
    return (
      <NewTempleForm
        onDone={(id) => {
          setCreating(false);
          if (id !== undefined) setOpenId(id);
        }}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-lg)', alignItems: 'start' }}>
      <Section
        title="Renewals due"
        note="Active agreements running out, soonest first"
        pad={false}
      >
        {expiring.isPending && <LoadingState message="Reading the renewal board…" />}
        {expiring.isError && (
          <ErrorState title="Could not read the renewal board" cause={errorMessage(expiring.error)} />
        )}
        {expiring.isSuccess && expiring.data.items.length === 0 && (
          <EmptyState icon="building" title="Nothing lapses in the next 60 days" inline />
        )}
        {expiring.data && expiring.data.items.length > 0 && (
          <DataTable
            density="compact"
            columns={[
              { key: 'temple', label: 'Temple' },
              { key: 'counterparty', label: 'Counterparty' },
              { key: 'expires', label: 'Expires', mono: true },
              { key: 'days', label: 'Days left', align: 'right', mono: true },
            ]}
            rows={expiring.data.items.map((row) => ({
              id: row.authorization.id,
              temple: row.temple_name,
              counterparty: row.authorization.counterparty_name,
              expires: row.authorization.expires_on ?? '—',
              days: (
                <StatusChip
                  tone={
                    expiring.data.warning_days.some((d) => row.days_to_expiry <= d)
                      ? 'warn'
                      : 'neutral'
                  }
                  label={String(row.days_to_expiry)}
                />
              ),
            }))}
            onRowClick={(row) => {
              const found = expiring.data!.items.find((i) => i.authorization.id === row.id);
              if (found) setOpenId(found.temple_id);
            }}
          />
        )}
      </Section>

      <Section
        title="Directory"
        note="Every temple page, whatever its state"
        pad={false}
        actions={
          <ScopeAction scope={scope} need="catalogue_content" size="sm" icon="plus" onClick={() => setCreating(true)}>
            New temple
          </ScopeAction>
        }
      >
        <FilterBar tabs={TABS} tab={tab} onTab={setTab} />
        {temples.isPending && <LoadingState message="Reading temples…" />}
        {temples.isError && (
          <ErrorState title="Could not read temples" cause={errorMessage(temples.error)} />
        )}
        {temples.isSuccess && rows.length === 0 && (
          <EmptyState icon="building" title="No temple in this state" inline />
        )}
        {rows.length > 0 && (
          <DataTable
            columns={[
              { key: 'name', label: 'Temple' },
              { key: 'slug', label: 'Page', mono: true },
              { key: 'status', label: 'State' },
            ]}
            rows={rows.map((t) => ({
              id: t.id,
              name: t.name,
              slug: `/temple/${t.slug}`,
              status: <StatusChip tone={statusTone(t.status)} label={t.status} />,
            }))}
            onRowClick={(row) => setOpenId(Number(row.id))}
          />
        )}
      </Section>
    </div>
  );
}
